import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, CheckCircle, Euro } from 'lucide-react';
import GrainTexture from '@/components/GrainTexture';
import { Button } from '@/components/ui/button';
import ThemadagDialog from '@/components/ThemadagDialog';
import { useToast } from '@/hooks/use-toast';
import { useSearchParams } from 'react-router-dom';
import { SEO } from '@/components/SEO';

const Themadag = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (success === 'true') {
      toast({
        title: "Betaling succesvol!",
        description: "Check je email voor de bevestiging van je aanmelding.",
      });
      // Cleanup sessionStorage
      sessionStorage.removeItem('themadag_form_data');
      // Remove query params
      setSearchParams({}, { replace: true });
    }

    if (canceled === 'true') {
      toast({
        title: "Betaling geannuleerd",
        description: "Je kunt het opnieuw proberen wanneer je klaar bent.",
        variant: "destructive",
      });
      // Remove query params
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, toast, setSearchParams]);

  return (
    <>
      <SEO 
        title="Systemisch werk bij Burn-out | Themadag Organisatieopstellingen & Verzuim"
        description="Verdiepende themadag over de systemische onderstroom van burn-out in organisaties. Voor HR en managers. Ontdek dynamieken via opstellingen. Bekijk programma."
        keywords="burn-out themadag, systemisch werk burn-out, organisatieopstellingen burn-out, burn-out preventie, HR burn-out, ziekteverzuim, duurzame inzetbaarheid"
        canonical="https://organisatieopstellingen.com/themadag"
        ogTitle="Systemisch werk bij Burn-out | Themadag Organisatieopstellingen & Verzuim"
        ogDescription="Verdiepende themadag over de systemische onderstroom van burn-out in organisaties. Voor HR en managers. Ontdek dynamieken via opstellingen."
        ogImage="https://organisatieopstellingen.com/themadag.png"
        ogImageWidth={1200}
        ogImageHeight={630}
        ogType="website"
        twitterCard="summary_large_image"
      />
      
      {/* FAQPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Wat is de Themadag Burn-out?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "De Themadag Burn-out is een verdiepende dag over de systemische onderstroom van burn-out in organisaties. We duiken een dag dieper in de thema's die in organisaties spelen als het gaat om burn-out. Waar is de burn-out in de organisatiecontext mee verbonden? De themadag is speciaal voor HR-professionals en managers die de thema's werkgeluk, duurzame inzetbaarheid en ziekteverzuim nauw aan het hart gaat."
                }
              },
              {
                "@type": "Question",
                "name": "Voor wie is de Themadag geschikt?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "De Themadag Burn-out is speciaal voor HR-professionals en managers die de thema's werkgeluk, duurzame inzetbaarheid en ziekteverzuim nauw aan het hart gaat. De themadag vindt plaats op 22 oktober 2026 bij Stadsoase naar Zee, Amsterdam (IJburg). Marthe van der Molen begeleidt deze themadag waarin we dieper ingaan op de systemische dynamieken achter burn-out."
                }
              },
              {
                "@type": "Question",
                "name": "Wat leer je op de Themadag?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Op de Themadag Burn-out leer je over de systemische dynamieken achter burn-out in organisaties. We duiken dieper in de thema's die spelen als het gaat om burn-out en ontdekken waar de burn-out in de organisatiecontext mee verbonden is. De themadag maakt gebruik van systemische werkvormen en opstellingen om inzicht te krijgen in de onderstroom van burn-out."
                }
              }
            ]
          })
        }}
      />
      <meta name="twitter:description" content="Themadag over burn-out. Ontdek de systemische dynamieken achter burn-out en creëer ruimte voor herstel." />
      <meta name="twitter:image" content="https://organisatieopstellingen.com/themadag.png" />
      
      {/* Event Schema - Themadag Burn-out */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationEvent",
            "name": "Themadag: Burn-out in organisaties",
            "description": "We duiken een dag dieper in de thema's die in organisaties spelen als het gaat om burn-out. Waar is de burn-out in de organisatiecontext mee verbonden? Speciaal voor HR-adviseurs en managers.",
            "startDate": "2026-10-22T13:00:00+02:00",
            "endDate": "2026-10-22T17:00:00+02:00",
            "eventStatus": "https://schema.org/EventScheduled",
            "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
            "image": ["https://organisatieopstellingen.com/themadag.png"],
            "url": "https://organisatieopstellingen.com/themadag",
            "location": {
              "@type": "Place",
              "name": "Stadsoase naar Zee",
              "url": "https://www.stadsoasenaarzee.com",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Bert Haanstrakade 102",
                "addressLocality": "Amsterdam",
                "postalCode": "1087 DN",
                "addressRegion": "Noord-Holland",
                "addressCountry": "NL"
              }
            },
            "organizer": {
              "@type": "Organization",
              "name": "Systemique",
              "url": "https://organisatieopstellingen.com",
              "email": "marthe@systemique.nl",
              "telephone": "+31-6-12991453"
            },
            "performer": {
              "@type": "Person",
              "name": "Marthe van der Molen"
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "HR-adviseurs, managers en professionals die werken met werkgeluk, duurzame inzetbaarheid en ziekteverzuim"
            },
            "offers": [
              {
                "@type": "Offer",
                "name": "Early Bird Ticket",
                "price": "100.00",
                "priceCurrency": "EUR",
                "availability": "https://schema.org/InStock",
                "validThrough": "2026-06-01T23:59:59+02:00",
                "url": "https://organisatieopstellingen.com/themadag"
              },
              {
                "@type": "Offer",
                "name": "Regulier Ticket",
                "price": "150.00",
                "priceCurrency": "EUR",
                "availability": "https://schema.org/InStock",
                "validFrom": "2026-06-02T00:00:00+02:00",
                "url": "https://organisatieopstellingen.com/themadag"
              }
            ]
          })
        }}
      />
      
      {/* Header Section */}
      <section className="relative pt-32 pb-16 px-6 bg-[#F2EADF] overflow-hidden">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          {/* Event Badge */}
          <div className="inline-block mb-6">
            <span className="px-4 py-1.5 border border-[#854C73] rounded-full text-xs font-bold uppercase tracking-widest text-[#854C73]">
              Evenement
            </span>
          </div>
          
          {/* Main Title */}
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight">
            <span className="font-bold" style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 700, color: '#854C73' }}>Themadag:</span> <br />
            <span className="italic font-light" style={{ fontFamily: "'Noto Serif Display', serif", color: '#915e84' }}>Burn-out in organisaties</span>
          </h1>
          
          {/* Quote */}
          <p className="font-serif text-xl md:text-2xl text-[#854C73]/80 italic max-w-3xl mx-auto leading-relaxed">
            "Daar waar het organisatiesysteem en familiesysteem elkaar raken"
          </p>
        </div>
      </section>

      {/* Details Card */}
      <section className="py-12 px-4 md:px-6 relative -mt-12 z-30">
        <div className="container mx-auto max-w-5xl">
          <div className="bg-[#F9EBEF] rounded-3xl p-8 md:p-12 shadow-xl border border-[#854C73]/10">
            <div className="flex flex-col md:flex-row justify-between items-start gap-8">
              {/* Left: Details */}
              <div className="flex flex-col gap-6 w-full">
                <div className="flex items-center gap-4 text-[#854C73]">
                  {/* Calendar with pulse animation */}
                  <div className="relative">
                    <div className="bg-[#854C73] text-[#F9EBEF] p-3 rounded-full animate-pulse-icon relative">
                      <Calendar size={24} />
                    </div>
                    <div className="absolute -inset-1 bg-[#854C73] rounded-full opacity-20 animate-ping"></div>
                  </div>
                  <div>
                    <span className="block text-xs uppercase tracking-widest opacity-60 mb-1">Datum</span>
                    <span className="text-xl font-bold">22 oktober 2026</span>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-[#854C73]">
                  <div className="bg-[#854C73] text-[#F9EBEF] p-3 rounded-full">
                    <Clock size={24} />
                  </div>
                  <div>
                    <span className="block text-xs uppercase tracking-widest opacity-60 mb-1">Tijd</span>
                    <span className="text-xl font-bold">13:00 - 17:00 u</span>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-[#854C73]">
                  <div className="bg-[#854C73] text-[#F9EBEF] p-3 rounded-full">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <span className="block text-xs uppercase tracking-widest opacity-60 mb-1">Locatie</span>
                    <a 
                      href="https://www.stadsoasenaarzee.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xl font-bold hover:underline"
                    >
                      Stadsoase naar Zee, Amsterdam
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-[#854C73]">
                  <div className="bg-[#854C73] text-[#F9EBEF] p-3 rounded-full">
                    <Euro size={24} />
                  </div>
                  <div>
                    <span className="block text-xs uppercase tracking-widest opacity-60 mb-1">Investering</span>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xl font-bold">€ 150</span>
                      <span className="text-sm text-[#854C73]/70 font-light">excl. BTW</span>
                    </div>
                    <span className="text-sm text-[#854C73]/80 font-light block mt-1">tot 1 juni € 100 excl. BTW</span>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px w-full md:w-px md:h-40 bg-[#854C73]/20"></div>

              {/* Right: Audience */}
              <div className="w-full md:w-2/3">
                <h2 className="font-serif text-2xl text-[#854C73] mb-4">Voor HR-adviseurs en Managers</h2>
                <p className="text-[#854C73]/80 leading-relaxed font-light">
                  Speciaal voor <span className="font-bold text-[#590D0D]">HR-adviseurs</span>, <span className="font-bold text-[#590D0D]">managers</span> en eenieder die de thema's werkgeluk, duurzame inzetbaarheid en ziekteverzuim nauw aan het hart gaat.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Verzuimpreventie Section */}
      <section className="py-16 px-6 relative overflow-hidden bg-white">
        <div className="container mx-auto max-w-4xl relative z-10">
          <h2 className="font-serif text-3xl md:text-4xl text-[#854C73] mb-6 text-center">
            Verzuimpreventie door <span className="italic font-light font-serif">Systemische Inzichten</span>
          </h2>
          <div className="prose prose-lg max-w-none text-[#854C73]/80 leading-relaxed">
            <p className="font-light text-lg mb-4">
              Burn-out en ziekteverzuim zijn vaak symptomen van dieperliggende systemische patronen. 
              Via organisatieopstellingen krijg je inzicht in de dynamieken die verzuim veroorzaken en leer je 
              hoe je deze preventief kunt aanpakken.
            </p>
            <p className="font-light text-lg">
              Deze themadag geeft HR-professionals en managers praktische tools om systemische oorzaken van 
              burn-out te herkennen en te doorbreken, waardoor verzuimpreventie effectiever wordt.
            </p>
          </div>
        </div>
      </section>

      {/* Program Section */}
      <section className="py-24 px-6 relative overflow-hidden bg-[#F2EADF]">
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="grid md:grid-cols-[1fr_1.5fr] gap-12">
            {/* Left: Inzicht in de onderstroom van burn-outs */}
            <div className="text-[#854C73] leading-relaxed space-y-6">
              <h3 className="font-serif text-3xl mb-2">
                Inzicht in de onderstroom van <span className="italic font-light font-serif">burn-outs</span>
              </h3>
              <p className="font-light">
                We duiken een dag dieper in de thema's die in organisaties spelen als het gaat om burn-out. 
                Waar is de burn-out in de organisatiecontext mee verbonden?
              </p>
              <p className="font-light">
                Het is geen standaard opstellingendag. We gaan experimenteren en ervaren.
              </p>
              
              {/* DE DYNAMIEKEN Box */}
              <div className="p-6 bg-[#76622F]/10 rounded-xl border border-[#76622F]/20 mt-8">
                <h4 className="font-bold text-[#76622F] uppercase tracking-wider text-sm mb-3">DE DYNAMIEKEN</h4>
                <ul className="space-y-3">
                  {[
                    {
                      name: 'Plekverwarring'
                    },
                    {
                      name: 'Niet genomen rouw'
                    },
                    {
                      name: 'Passie in de schaduw'
                    }
                  ].map((item, i) => {
                    return (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle size={16} className="text-[#76622F] mt-0.5 flex-shrink-0" />
                        <span className="font-light text-[#76622F]">
                          {item.name}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            {/* Right: Programma Timeline */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-[#854C73]/10 relative overflow-hidden">
              <GrainTexture opacity={0.1} />
              <h2 className="font-serif text-3xl text-[#854C73] mb-8 relative z-10">Programma <span className="italic font-light font-serif">Themadag</span></h2>
              
              <div className="space-y-0 relative z-10 ml-3">
                {/* Static background line */}
                <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#854C73]/10"></div>
                
                {/* Animated pulsing line traveling down */}
                <div className="absolute left-0 w-[2px] h-[150px] bg-gradient-to-b from-transparent via-[#854C73] to-transparent animate-drop z-0"></div>

                {[
                  { time: "13:00", title: "Opstart & welkom", desc: "Gezamenlijke start en landing" },
                  { time: "13:30", title: "Onderliggende dynamieken", desc: "Hoe werken ze door op organisatieniveau?" },
                  { time: "14:00", title: "Opstellingen over burn-out in organisaties", desc: "Inzichtelijk maken van patronen" },
                  { time: "16:30", title: "Rode draad ophalen", desc: "Wat nemen we mee terug naar onze eigen organisatie?" },
                  { time: "17:00", title: "Dagafsluiting", desc: "Er is daarna gelegenheid voor een borrel/napraten" }
                ].map((item, i) => (
                  <div key={i} className="relative pl-8 pb-8 last:pb-0 z-10">
                    {/* Static Dot */}
                    <div className="absolute left-[-7px] top-0 w-4 h-4 rounded-full bg-[#854C73] border-4 border-white"></div>
                    
                    {item.time && (
                      <span className="text-xs font-bold text-[#76622F] uppercase tracking-wider mb-1 block">
                        {item.time}
                      </span>
                    )}
                    <h4 className="font-bold text-[#854C73] text-lg">{item.title}</h4>
                    <p className="text-sm text-[#854C73]/60 font-light">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-20">
            <Button 
              size="lg" 
              className="rounded-full bg-[#854C73] text-[#F9EBEF] hover:bg-[#590D0D] hover:scale-105 transition-all shadow-xl"
              onClick={() => setDialogOpen(true)}
            >
              Meld je nu aan voor deze dag
            </Button>
            <p className="mt-4 text-sm text-[#854C73]/60 font-light">Beperkt aantal plaatsen beschikbaar.</p>
          </div>
        </div>
      </section>
      
      <ThemadagDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
};

export default Themadag;

